import '@/utils/logger'

import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import './plugins'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { Socket, socket } from '@/utils/Socket'
import './assets/css/tailwind.css'
import {
  darkModeProvider,
  darkModeProviderKey,
  themeProvider,
  themeProviderKey,
} from '@/utils/theme.util'
import PrimeVue from 'primevue/config'
import ToastService from 'primevue/toastservice'
import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'
import { createPinia } from 'pinia'
import { VueReCaptcha } from 'vue-recaptcha-v3'
import 'primeicons/primeicons.css'
import { init as initTelegram } from '@tma.js/sdk'

import { environment } from './environments/environment'
import {registerKeyboardScrollListener, vScrollIntoView} from "@/composables/useScrollIntoView";
import {isTMA} from "@tma.js/sdk-vue";

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $socket: typeof Socket
  }
}

// --- Custom theme ---
const customAura = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#f7ffe0',
      100: '#ecffb3',
      200: '#e1ff80',
      300: '#d6ff4d',
      400: '#ceff26',
      500: '#caff01',
      600: '#b6e601',
      700: '#9ecc01',
      800: '#86b201',
      900: '#6e9900',
      950: '#5c8000',
    },
    colorScheme: {
      light: {
        primary: { color: '#3b3b00', inverseColor: '#caff01', hoverColor: '#4d4d00', activeColor: '#5c5c00' },
        highlight: { background: '#3b3b00', focusBackground: '#4d4d00', color: '#caff01', focusColor: '#caff01' },
      },
      dark: {
        primary: { color: '#ceff26', inverseColor: '#000000', hoverColor: '#9ecc01', activeColor: '#b6e601' },
        highlight: { background: '#86b201', focusBackground: '#9ecc01', color: '#ffffff', focusColor: '#ffffff' },
      },
    },
  },
})

// --- Dynamic manifest & title helper ---
function applyTitle(mode: string) {
  const existingAppleTouch = document.querySelector('link[rel="apple-touch-icon"]')
  if (existingAppleTouch) existingAppleTouch.remove()

  const appleTouchLink = document.createElement('link')
  appleTouchLink.rel = 'apple-touch-icon'

  let appTitle = 'Bet Share'
  switch (mode) {
    case 'odds':
      appleTouchLink.href = '/img/icons/odds/apple-touch-icon-180x180.png'
      appTitle = 'Bet Share - Odds'
      break
    case 'direct':
      appleTouchLink.href = '/img/icons/bet/apple-touch-icon-180x180.png'
      appTitle = 'Bet Share - Direct'
      break
    default:
      appleTouchLink.href = `${environment.API_CLIENT}/img/icons/bet/apple-touch-icon-180x180.png`
  }

  document.head.appendChild(appleTouchLink)

  // iOS home screen meta
  let appleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]') as HTMLMetaElement
  if (!appleMeta) {
    appleMeta = document.createElement('meta')
    appleMeta.name = 'apple-mobile-web-app-title'
    document.head.appendChild(appleMeta)
  }
  appleMeta.content = appTitle
}

// --- Create Vue app ---
const pinia = createPinia()
const app = createApp(App)
app.use(pinia)

app.use(PrimeVue, {
  theme: {
    preset: customAura,
    options: { prefix: 'p', cssLayer: false, darkModeSelector: '.dark' },
  },
})

const customRecaptchaSiteKey = window.__APP_CONFIG__?.recaptchaSiteKey
app.use(VueReCaptcha, {
  siteKey: customRecaptchaSiteKey || import.meta.env.VITE_RECAPTCHA_SITE_KEY,
  loaderOptions: { autoHideBadge: true },
})

app.use(ToastService)

const customGoogleClientId = window.__APP_CONFIG__?.googleClientId
// console.log('Google Client ID:', customGoogleClientId || import.meta.env.VITE_GOOGLE_CLIENT_ID)

app.use(router)
app.use(store)
app.component('font-awesome-icon', FontAwesomeIcon)

app.provide('socket', socket)
app.provide(darkModeProviderKey, darkModeProvider)
app.provide(themeProviderKey, themeProvider)

app.directive('scroll-into-view', vScrollIntoView)
registerKeyboardScrollListener()

app.config.globalProperties.$socket = socket

const isTelegram = isTMA()

if (isTelegram) {
  try {
    initTelegram()
  } catch (err) {
    console.warn('[Telegram SDK] Failed to initialize:', err)
  }
}

router.isReady().then(async () => {
  const operationMode: string = store.getters['configModule/operationMode']
  applyTitle(operationMode)

  if ('serviceWorker' in navigator) {
    try {
      const { default: registerDynamicServiceWorker } = await import('./registerServiceWorker')
      await registerDynamicServiceWorker()
      console.log('[PWA] SW registration done.')
    } catch (err) {
      console.error('[PWA] SW registration failed:', err)
    }
  }
  app.mount('#app')
})
